import React, { useEffect, useMemo, useState } from "react";
import { byggKategoriTre } from "../utils/kategoriTre";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Ved søk: behold en rad hvis navnet matcher, ELLER en etterkommer matcher
// (så man fortsatt ser hvor treffet ligger i treet).
function filtrerPåSøk(treRader, søk) {
  if (!søk.trim()) return treRader;
  const søkLav = søk.trim().toLowerCase();

  const barnIder = new Map();
  for (const rad of treRader) {
    const forelderNøkkel = rad.overordnetKategori ? String(rad.overordnetKategori) : "";
    if (!barnIder.has(forelderNøkkel)) barnIder.set(forelderNøkkel, []);
    barnIder.get(forelderNøkkel).push(rad);
  }

  const treffEllerEtterkommerTreffer = new Map();
  function sjekk(rad) {
    const id = String(rad._id);
    if (treffEllerEtterkommerTreffer.has(id)) return treffEllerEtterkommerTreffer.get(id);
    let treff = rad.navn.toLowerCase().includes(søkLav);
    for (const barn of barnIder.get(id) || []) {
      if (sjekk(barn)) treff = true;
    }
    treffEllerEtterkommerTreffer.set(id, treff);
    return treff;
  }
  for (const rad of treRader) sjekk(rad);

  return treRader.filter((rad) => treffEllerEtterkommerTreffer.get(String(rad._id)));
}

export default function Kategorier() {
  const [kategorier, setKategorier] = useState([]);
  const [lasterInn, setLasterInn] = useState(true);
  const [feil, setFeil] = useState("");
  const [søk, setSøk] = useState("");

  const [visSkjuleInaktive, setVisSkjuleInaktive] = useState(true);
  const [nyHovedkategoriNavn, setNyHovedkategoriNavn] = useState("");
  const [visNyHovedkategoriSkjema, setVisNyHovedkategoriSkjema] = useState(false);

  const [underskjemaForId, setUnderskjemaForId] = useState(null);
  const [underskjemaNavn, setUnderskjemaNavn] = useState("");

  const [redigererId, setRedigererId] = useState(null);
  const [redigererNavn, setRedigererNavn] = useState("");

  const [lagrer, setLagrer] = useState(false);

  async function hentKategorier() {
    setLasterInn(true);
    setFeil("");
    try {
      const res = await fetch(`${API_BASE_URL}/kategorier`);
      if (!res.ok) throw new Error("Kunne ikke hente kategorier");
      const data = await res.json();
      setKategorier(data);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLasterInn(false);
    }
  }

  useEffect(() => {
    hentKategorier();
  }, []);

  const treRader = useMemo(() => byggKategoriTre(kategorier), [kategorier]);
  const synligeRader = useMemo(() => {
    const base = visSkjuleInaktive ? treRader : treRader.filter((r) => r.aktiv);
    return filtrerPåSøk(base, søk);
  }, [treRader, søk, visSkjuleInaktive]);

  // Oppretter én eller flere kategorier under samme forelder i ett steg - skjemaene
  // under tar imot flere linjer (ett navn pr. linje), så en hel liste limt inn fra
  // f.eks. backoffice kan opprettes samlet i stedet for én og én.
  async function opprettKategorier(navnTekst, overordnetKategori) {
    const navnene = navnTekst
      .split("\n")
      .map((n) => n.trim())
      .filter(Boolean);
    if (!navnene.length) return;
    setLagrer(true);
    setFeil("");
    try {
      for (const navn of navnene) {
        const res = await fetch(`${API_BASE_URL}/kategorier`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ navn, overordnetKategori: overordnetKategori || null }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(`«${navn}»: ${data.error || "Kunne ikke opprette kategorien"}`);
      }
      await hentKategorier();
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  async function oppdaterKategori(id, endringer) {
    setLagrer(true);
    setFeil("");
    try {
      const res = await fetch(`${API_BASE_URL}/kategorier/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(endringer),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke oppdatere kategorien");
      await hentKategorier();
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  function startRedigering(rad) {
    setRedigererId(rad._id);
    setRedigererNavn(rad.navn);
    setUnderskjemaForId(null);
  }

  async function lagreRedigering(id) {
    const navn = redigererNavn.trim();
    if (!navn) return;
    await oppdaterKategori(id, { navn });
    setRedigererId(null);
  }

  async function leggTilUnder(forelderId) {
    if (!underskjemaNavn.trim()) return;
    await opprettKategorier(underskjemaNavn, forelderId);
    setUnderskjemaNavn("");
    setUnderskjemaForId(null);
  }

  async function opprettHovedkategori() {
    if (!nyHovedkategoriNavn.trim()) return;
    await opprettKategorier(nyHovedkategoriNavn, null);
    setNyHovedkategoriNavn("");
    setVisNyHovedkategoriSkjema(false);
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Kategorier</h1>
      <p className="text-sm text-gray-500 mb-6">
        Varegruppe/mellomgruppe/undergruppe-treet som styrer nettbutikkens kategorier —
        brukes både til å kategorisere produkter ved redigering og (når navnene matcher)
        automatisk ved import via Kategori-/Hovedkategori-kolonnene. En kategori fjernes
        aldri, kun deaktiveres (den kan ha produkter eller underkategorier knyttet til seg).
      </p>

      <div className="border border-gray-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1">
            <input
              type="text"
              value={søk}
              onChange={(e) => setSøk(e.target.value)}
              placeholder="Søk i kategorier …"
              className="w-full border border-gray-300 rounded-lg pl-3 pr-8 py-1.5 text-sm"
            />
            <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap px-1">
            <input
              type="checkbox"
              checked={visSkjuleInaktive}
              onChange={(e) => setVisSkjuleInaktive(e.target.checked)}
            />
            Vis deaktiverte
          </label>
          <button
            type="button"
            onClick={() => setVisNyHovedkategoriSkjema((v) => !v)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-semibold text-gray-700 whitespace-nowrap"
          >
            + Ny hovedkategori
          </button>
        </div>

        {visNyHovedkategoriSkjema && (
          <div className="flex flex-col sm:flex-row gap-2 mb-3 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
            <textarea
              autoFocus
              rows={3}
              value={nyHovedkategoriNavn}
              onChange={(e) => setNyHovedkategoriNavn(e.target.value)}
              onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && opprettHovedkategori()}
              placeholder={"Ett navn pr. linje, f.eks.\nFritidsbekledning\nFottøy\nVintersport"}
              className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
            />
            <button
              type="button"
              disabled={lagrer || !nyHovedkategoriNavn.trim()}
              onClick={opprettHovedkategori}
              className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-40 self-start whitespace-nowrap"
            >
              Opprett (⌘/Ctrl+Enter)
            </button>
          </div>
        )}

        {feil && (
          <p className="text-sm text-red-600 mb-3">{feil}</p>
        )}

        {lasterInn ? (
          <p className="text-sm text-gray-400 py-4">Laster inn …</p>
        ) : synligeRader.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {søk ? "Ingen kategorier matcher søket." : "Ingen kategorier ennå."}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {synligeRader.map((rad) => (
              <li key={rad._id} style={{ paddingLeft: `${rad.dybde * 1.25}rem` }} className="py-1.5">
                <div className="flex items-center gap-2">
                  {redigererId === rad._id ? (
                    <>
                      <input
                        type="text"
                        autoFocus
                        value={redigererNavn}
                        onChange={(e) => setRedigererNavn(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && lagreRedigering(rad._id)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                      />
                      <button
                        type="button"
                        disabled={lagrer}
                        onClick={() => lagreRedigering(rad._id)}
                        className="text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap"
                      >
                        Lagre
                      </button>
                      <button
                        type="button"
                        onClick={() => setRedigererId(null)}
                        className="text-xs text-gray-400 hover:underline whitespace-nowrap"
                      >
                        Avbryt
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`text-sm flex-1 ${rad.aktiv ? "text-gray-800" : "text-gray-400 italic"}`}>
                        {rad.navn}
                        {!rad.aktiv && <span className="ml-1.5 text-[11px] text-gray-400">(deaktivert)</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => startRedigering(rad)}
                        className="text-xs text-gray-400 hover:text-gray-700 whitespace-nowrap"
                      >
                        Endre navn
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setUnderskjemaForId(underskjemaForId === rad._id ? null : rad._id);
                          setUnderskjemaNavn("");
                          setRedigererId(null);
                        }}
                        className="text-xs text-gray-400 hover:text-gray-700 whitespace-nowrap"
                      >
                        + Underkategori
                      </button>
                      <button
                        type="button"
                        disabled={lagrer}
                        onClick={() => oppdaterKategori(rad._id, { aktiv: !rad.aktiv })}
                        className="text-xs text-gray-400 hover:text-gray-700 whitespace-nowrap"
                      >
                        {rad.aktiv ? "Deaktiver" : "Aktiver"}
                      </button>
                    </>
                  )}
                </div>

                {underskjemaForId === rad._id && (
                  <div
                    style={{ marginLeft: `${(rad.dybde + 1) * 1.25}rem` }}
                    className="flex flex-col sm:flex-row gap-2 mt-1.5 bg-gray-50 border border-gray-200 rounded-lg p-2"
                  >
                    <textarea
                      autoFocus
                      rows={3}
                      value={underskjemaNavn}
                      onChange={(e) => setUnderskjemaNavn(e.target.value)}
                      onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && leggTilUnder(rad._id)}
                      placeholder={`Ett navn pr. linje under «${rad.navn}»`}
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm font-mono"
                    />
                    <button
                      type="button"
                      disabled={lagrer || !underskjemaNavn.trim()}
                      onClick={() => leggTilUnder(rad._id)}
                      className="px-2.5 py-1 rounded-lg bg-gray-900 text-white text-xs font-semibold disabled:opacity-40 self-start whitespace-nowrap"
                    >
                      Opprett (⌘/Ctrl+Enter)
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
